// src/views/boletos/RetornoCNAB.jsx
import React, { useState, useEffect, useMemo } from "react";
import cobrancasService from "../../services/cobrancasService";
import condominiosService from "../../services/condominiosService";
import contasBancariasService from "../../services/contasBancariasService";
import { useAuth } from "../../contexts/AuthContext";

const fmtM = v => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDt = v => { try { return new Date(v + "T12:00:00").toLocaleDateString("pt-BR"); } catch { return v; } };

// ── Banner ──────────────────────────────────────────────────────────────
function Banner({ tipo = "info", children, onClose }) {
  const cores = {
    error:   { bg: "#FEE2E2", border: "#FECACA", color: "#991B1B", icon: "bi-exclamation-octagon" },
    success: { bg: "#DCFCE7", border: "#BBF7D0", color: "#166534", icon: "bi-check-circle" },
    info:    { bg: "#EFF6FF", border: "#BFDBFE", color: "#1D4ED8", icon: "bi-info-circle" },
    warning: { bg: "#FEF3C7", border: "#FDE68A", color: "#92400E", icon: "bi-exclamation-triangle" },
  };
  const c = cores[tipo] || cores.info;
  if (!children) return null;
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.color, borderRadius: 8,
      padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10, fontSize: 13.5,
    }}>
      <i className={`bi ${c.icon}`} style={{ fontSize: 18 }} />
      <div style={{ flex: 1 }}>{children}</div>
      {onClose && (
        <button className="btn btn-sm" style={{ padding: "2px 8px", background: "transparent", color: c.color }} onClick={onClose}>
          <i className="bi bi-x-lg" />
        </button>
      )}
    </div>
  );
}

export default function RetornoCNAB() {
  const { usuario, condominioAtivo, podeEditar } = useAuth();
  const canEdit = podeEditar("retornoCnab") || podeEditar("retorno-cnab");
  const isSindico = usuario?.perfil === "Sindico";

  const [condominios, setCondominios] = useState([]);
  const [contas,      setContas]      = useState([]);
  const [idCondo,     setIdCondo]     = useState("");
  const [idConta,     setIdConta]     = useState("");
  const [arquivo,     setArquivo]     = useState(null);
  const [processing,  setProcessing]  = useState(false);
  const [resultado,   setResultado]   = useState(null);
  const [pageError,   setPageError]   = useState("");
  const [pageSuccess, setPageSuccess] = useState("");

  // Carrega condomínios (filtra para Síndico)
  useEffect(() => {
    if (isSindico) {
      setCondominios(usuario.condominios || []);
    } else {
      condominiosService.listar({ limit: 100 }).then(r => setCondominios(r.data || [])).catch(() => {});
    }
  }, [usuario, isSindico]);

  // Auto-seleciona o condomínio ativo da navbar
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
  }, [condominioAtivo]);

  useEffect(() => {
    if (!idCondo) { setContas([]); return; }
    contasBancariasService.listar(idCondo).then(r => setContas(r.data || [])).catch(() => {});
  }, [idCondo]);

  useEffect(() => {
    if (!pageSuccess) return;
    const t = setTimeout(() => setPageSuccess(""), 5000);
    return () => clearTimeout(t);
  }, [pageSuccess]);

  const handleArquivo = e => {
    const f = e.target.files[0];
    if (!f) return;
    setArquivo(f);
    setResultado(null);
    setPageError("");
  };

  const handleProcessar = async () => {
    if (!arquivo) { setPageError("Selecione o arquivo de retorno."); return; }
    if (!idCondo) { setPageError("Selecione o condomínio."); return; }
    setProcessing(true); setPageError(""); setResultado(null);
    try {
      const conteudo = await arquivo.text();
      const r = await cobrancasService.processarRetorno({
        conteudo, idCondominio: idCondo, idContaBancaria: idConta || null,
      });
      setResultado(r);
      setPageSuccess(r.message || "Processamento concluído.");
    } catch (e) {
      setPageError(e?.message || "Falha ao processar arquivo.");
    } finally { setProcessing(false); }
  };

  const condoSel = useMemo(
    () => condominios.find(c => String(c.id) === String(idCondo)),
    [condominios, idCondo]
  );

  const exportarResumoCSV = () => {
    if (!resultado) return;
    const linhas = [];
    linhas.push(["Tipo", "Nosso Número", "Vencimento", "Dt. Pagamento", "Valor", "Detalhe"]);
    (resultado.processados || []).forEach(p => linhas.push([
      "Baixado", p.nossoNumero, "", p.dtPagamento || "", p.vlrPago || "", p.status,
    ]));
    (resultado.rejeitados || []).forEach(r => linhas.push([
      "Rejeitado", r.nossoNumero, r.dtVencimento || "", "", r.vlrTitulo || "", `Oc.${r.codOcorrencia}`,
    ]));
    (resultado.erros || []).forEach(e => linhas.push([
      "Erro",
      typeof e === "string" ? "" : e.nossoNumero || "",
      "", "", "",
      typeof e === "string" ? e : e.motivo,
    ]));
    const csv = linhas.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `retorno-cnab-${resultado.banco || "resumo"}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const imprimirResumo = () => {
    if (!resultado) return;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;
    const linhasProc = (resultado.processados || []).map(p =>
      `<tr><td>${p.nossoNumero}</td><td>${p.dtPagamento ? fmtDt(p.dtPagamento) : "—"}</td><td style="text-align:right">${p.vlrPago ? fmtM(p.vlrPago) : "—"}</td><td>${p.status}</td></tr>`).join("");
    const linhasRej = (resultado.rejeitados || []).map(r =>
      `<tr><td>${r.nossoNumero}</td><td>${r.dtVencimento ? fmtDt(r.dtVencimento) : "—"}</td><td style="text-align:right">${fmtM(r.vlrTitulo)}</td><td>Oc.${r.codOcorrencia}</td></tr>`).join("");
    const linhasErr = (resultado.erros || []).map(e =>
      `<tr><td colspan="4">${typeof e === "string" ? e : `${e.nossoNumero}: ${e.motivo}`}</td></tr>`).join("");

    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Resumo Retorno CNAB</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#111}
        h1{color:#1B3A6B;margin-bottom:4px}
        .meta{font-size:12px;color:#64748B;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;margin-bottom:18px;font-size:12.5px}
        th{background:#F8FAFC;text-align:left;padding:6px 8px;border-bottom:2px solid #E2E8F0}
        td{padding:6px 8px;border-bottom:1px solid #F1F5F9}
        h2{font-size:14px;color:#1B3A6B;margin-top:18px}
      </style></head><body>
      <h1>Resumo Retorno CNAB</h1>
      <div class="meta">
        <strong>Condomínio:</strong> ${condoSel?.nmFantasia || condoSel?.nmCondominio || "—"}<br/>
        <strong>Banco:</strong> ${resultado.banco || "—"} ·
        <strong>Arquivo:</strong> ${resultado.dtArquivo ? fmtDt(resultado.dtArquivo) : "—"} ·
        <strong>Baixados:</strong> ${resultado.processados?.length || 0} ·
        <strong>Rejeitados:</strong> ${resultado.rejeitados?.length || 0} ·
        <strong>Erros:</strong> ${resultado.erros?.length || 0}
      </div>
      ${linhasProc ? `<h2>Boletos Baixados</h2><table><thead><tr><th>Nosso Nº</th><th>Dt. Pgto</th><th style="text-align:right">Valor</th><th>Status</th></tr></thead><tbody>${linhasProc}</tbody></table>` : ""}
      ${linhasRej ? `<h2>Rejeitados pelo banco</h2><table><thead><tr><th>Nosso Nº</th><th>Vencimento</th><th style="text-align:right">Valor</th><th>Oc.</th></tr></thead><tbody>${linhasRej}</tbody></table>` : ""}
      ${linhasErr ? `<h2>Erros</h2><table><tbody>${linhasErr}</tbody></table>` : ""}
      <script>window.onload = () => setTimeout(()=>window.print(),300);</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div>
      <Banner tipo="error"   onClose={() => setPageError("")}>{pageError}</Banner>
      <Banner tipo="success" onClose={() => setPageSuccess("")}>{pageSuccess}</Banner>

      <div className="page-header">
        <div>
          <h1 className="page-title"><i className="bi bi-file-earmark-arrow-down me-2" style={{ color: "var(--primary)" }} />Retorno CNAB</h1>
          <p className="page-subtitle">Processa arquivo de retorno bancário e realiza baixa automática dos boletos</p>
        </div>
      </div>

      {!canEdit && (
        <Banner tipo="warning">Você está em modo somente leitura. Para processar arquivos, é necessária permissão de edição neste módulo.</Banner>
      )}

      <div style={{ padding: 16 }}>
        <div className="form-section" style={{ maxWidth: 760 }}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Condomínio <span className="required-mark">*</span></label>
              <select className="form-select" value={idCondo}
                onChange={e => { setIdCondo(e.target.value); setIdConta(""); }}>
                <option value="">— Selecione —</option>
                {condominios.map(c => <option key={c.id} value={c.id}>{c.nmFantasia || c.nmCondominio}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Conta Bancária (opcional)</label>
              <select className="form-select" value={idConta} onChange={e => setIdConta(e.target.value)}>
                <option value="">— Para lançar transação —</option>
                {contas.map(c => <option key={c.id} value={c.id}>{c.nmConta}</option>)}
              </select>
              <small className="text-muted">Se informada, cria transação bancária e atualiza saldo</small>
            </div>
            <div className="col-12">
              <label className="form-label">Arquivo de Retorno (.ret / .txt) <span className="required-mark">*</span></label>
              <input type="file" className="form-control" accept=".ret,.txt,.RET,.TXT" onChange={handleArquivo} disabled={!canEdit} />
              <small className="text-muted">Suporte: Sicoob (756), EFÍ/Itaú (341/364) — CNAB240</small>
            </div>
            {arquivo && (
              <div className="col-12">
                <div style={{ background:"var(--bg-success-soft)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#166534" }}>
                  <i className="bi bi-file-earmark-check me-2" />
                  <strong>{arquivo.name}</strong> — {(arquivo.size / 1024).toFixed(1)} KB
                </div>
              </div>
            )}
            <div className="col-12">
              <button className="btn btn-primary" onClick={handleProcessar} disabled={processing || !arquivo || !idCondo || !canEdit}>
                {processing
                  ? <><span className="spinner-border spinner-border-sm me-2" />Processando...</>
                  : <><i className="bi bi-play-circle me-2" />Processar Retorno</>}
              </button>
            </div>
          </div>
        </div>

        {/* Resultado */}
        {resultado && (
          <div style={{ marginTop: 24 }}>
            {/* Header */}
            <div style={{
              background: "#1B3A6B", color: "#fff", borderRadius: "10px 10px 0 0",
              padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center",
              flexWrap: "wrap", gap: 10,
            }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: 15 }}>
                  <i className="bi bi-bank me-2" />Retorno {resultado.banco}
                </span>
                {resultado.dtArquivo && <span style={{ fontSize: 12, opacity: .8, marginLeft: 12 }}>
                  Arquivo: {fmtDt(resultado.dtArquivo)}
                </span>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-sm btn-light" onClick={exportarResumoCSV} title="Exportar resumo em CSV">
                  <i className="bi bi-filetype-csv me-1" /> CSV
                </button>
                <button className="btn btn-sm btn-light" onClick={imprimirResumo} title="Imprimir resumo">
                  <i className="bi bi-printer me-1" /> Imprimir
                </button>
              </div>
            </div>

            {/* Cards totais */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 0, marginBottom: 16 }}>
              {[
                { label: "Baixados",    val: resultado.processados?.length || 0, sub: fmtM(resultado.totais?.vlrPago || 0), color: "#166534", bg: "#DCFCE7" },
                { label: "Rejeitados",  val: resultado.rejeitados?.length || 0,  sub: "Ocorrências banco",                  color: "#991B1B", bg: "#FEE2E2" },
                { label: "Erros",       val: resultado.erros?.length || 0,       sub: "Não encontrados",                    color: "#92400E", bg: "#FEF3C7" },
                { label: "Valor Total", val: fmtM(resultado.totais?.vlrPago || 0), sub: "Liquidado",                        color: "#1D4ED8", bg: "#DBEAFE" },
              ].map((c, i) => (
                <div key={i} style={{ background: c.bg, padding: "14px 20px", borderRight: i < 3 ? "1px solid rgba(0,0,0,.06)" : "none" }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.val}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: c.color, opacity: .8 }}>{c.label}</div>
                  <div style={{ fontSize: 11, color:"var(--gray-500)", marginTop: 2 }}>{c.sub}</div>
                </div>
              ))}
            </div>

            {/* Processados */}
            {resultado.processados?.length > 0 && (
              <div className="form-section" style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#166534", marginBottom: 12 }}>
                  <i className="bi bi-check-circle me-2" />Boletos Baixados ({resultado.processados.length})
                </div>
                <table className="table table-custom" style={{ fontSize: 13 }}>
                  <thead><tr>
                    <th>Nosso Número</th><th>Dt. Pagamento</th>
                    <th style={{ textAlign: "right" }}>Valor Pago</th><th>Status</th>
                  </tr></thead>
                  <tbody>
                    {resultado.processados.map((p, i) => (
                      <tr key={i}>
                        <td style={{ fontFamily: "monospace", fontSize: 12 }}>{p.nossoNumero}</td>
                        <td>{p.dtPagamento ? fmtDt(p.dtPagamento) : "—"}</td>
                        <td style={{ textAlign: "right", fontWeight: 600, color: "#166534" }}>{p.vlrPago ? fmtM(p.vlrPago) : "—"}</td>
                        <td><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background:"var(--bg-success-soft)", color: "#166534", fontWeight: 700 }}>{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Rejeitados */}
            {resultado.rejeitados?.length > 0 && (
              <div className="form-section" style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#991B1B", marginBottom: 12 }}>
                  <i className="bi bi-x-circle me-2" />Rejeitados pelo banco ({resultado.rejeitados.length})
                </div>
                <table className="table table-custom" style={{ fontSize: 13 }}>
                  <thead><tr><th>Nosso Número</th><th>Vencimento</th><th style={{ textAlign: "right" }}>Valor</th><th>Código Oc.</th></tr></thead>
                  <tbody>
                    {resultado.rejeitados.map((r, i) => (
                      <tr key={i}>
                        <td style={{ fontFamily: "monospace", fontSize: 12 }}>{r.nossoNumero}</td>
                        <td>{r.dtVencimento ? fmtDt(r.dtVencimento) : "—"}</td>
                        <td style={{ textAlign: "right" }}>{fmtM(r.vlrTitulo)}</td>
                        <td><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background:"var(--bg-danger-soft)", color: "#991B1B", fontWeight: 700 }}>{r.codOcorrencia}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Erros */}
            {resultado.erros?.length > 0 && (
              <div className="form-section">
                <div style={{ fontWeight: 700, fontSize: 14, color: "#92400E", marginBottom: 12 }}>
                  <i className="bi bi-exclamation-triangle me-2" />Erros ({resultado.erros.length})
                </div>
                {resultado.erros.map((e, i) => (
                  <div key={i} style={{ fontSize: 12, padding: "6px 12px", background: "#FEF3C7", borderRadius: 6, marginBottom: 4, color: "#92400E" }}>
                    {typeof e === "string" ? e : `${e.nossoNumero}: ${e.motivo}`}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
